import { useDebounce, useGetJob } from "@api";
import type { IJob } from "@bbj-types/job";
import { type ChangeEvent, type SyntheticEvent, useCallback, useEffect, useState } from "react";
import { useModalOfferContext } from "../context";

export const useModalOfferAppellations = () => {
  const { offer, setOffer, setAppellation, setErrors } = useModalOfferContext();
  const [{ loading: loadingGetJob }, getJobs, cancel] = useGetJob();
  const [appellationsOptions, setAppellationsOptions] = useState<IJob[]>([]);

  const handleChange = useCallback(
    (_: SyntheticEvent<Element, Event> | null, value: IJob | null) => {
      if (!value) {
        setAppellation(null);
      } else if (value && typeof value !== "string") {
        setAppellation(value);
        setErrors((errors) => ({ ...errors, appellation: false }));
      }
    },
    [setAppellation, setErrors]
  );

  const handleGetJob = useDebounce(
    async (jobInput: string, isOfferName: boolean) => {
      try {
        if (jobInput) {
          const result = await getJobs({ libelle: jobInput });
          if (result.status === 200) {
            setAppellationsOptions(result.data.data);
            if (isOfferName) {
              handleChange(null, result.data.data[0] ?? null);
            }
          }
        }
      } catch (error) {
        window.publish("feedback", {
          severity: "error",
          message: "Aucune appellation n'a été trouvée.",
        });
      }
    },
    750,
    [getJobs]
  );

  const loadOnType = useCallback(
    (value: string, isOfferName: boolean) => {
      if (loadingGetJob) {
        cancel();
      }
      handleGetJob(value, isOfferName);
    },
    [handleGetJob, loadingGetJob, cancel]
  );

  const handleInputChange = useCallback(
    (event: SyntheticEvent<Element, Event>, value: string, isOfferName = false) => {
      if (value && event && event.type === "change") {
        loadOnType(value, isOfferName);
      }
    },
    [loadOnType]
  );

  const handleOfferName = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setOffer((offer) => {
        return { ...offer, name: event.target.value };
      });
      if (event.target.value !== "") {
        handleInputChange(event, event.target.value, true);
      } else {
        setAppellation(null);
        setAppellationsOptions([]);
      }
      if (offer.name) {
        setErrors((errors) => ({ ...errors, name: false }));
      }
    },
    [handleInputChange, offer.name, setAppellation, setErrors, setOffer]
  );

  useEffect(() => {
    void (async () => {
      if (offer.appellationCode) {
        const result = await getJobs({ ids: [offer.appellationCode] });
        if (result.status === 200) {
          setAppellationsOptions(result.data.data);
          if (result.data.data[0]) {
            handleChange(null, result.data.data[0]);
          }
        }
      }
    })();
  }, [offer.appellationCode]);

  return {
    appellationsOptions,
    setAppellationsOptions,
    loadingGetJob,
    handleChange,
    handleGetJob,
    handleInputChange,
    handleOfferName,
  };
};
